import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SparkleIcon, CheckCircleIcon } from '@phosphor-icons/react';
import { useUser } from '../../../utils/UserContext';
import { Entity, ApplicationStep } from '../../../types/apiTypes';
import { Application } from '../../../types/dbTypes';
import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../../../shared/safeStorage';

interface CompleteOnboardingModalProps {
  application: Application | undefined;
  isPreActive: boolean;
}

const REMIND_LATER_MS = 3 * 60 * 1000; // 3 minutes

export default function CompleteOnboardingModal({
  application,
  isPreActive,
}: CompleteOnboardingModalProps) {
  const [visible, setVisible] = useState(true);
  /**
   * There is nothing left to remind them about once they are awaiting review, and every path that
   * finishes onboarding lands there: Plaid (PlaidLink.tsx), the retry (PlaidLinkRetry.tsx) and the
   * bank-statement upload (DocumentUpload.tsx) all advance to pending_review.
   *
   * Keyed off the persisted step rather than the "they pressed the button" flag below, so it can't
   * drift from what onboarding actually did — a customer bounced back to plaid_link_retry or
   * manual_document_upload reopens this by itself, where a stored flag would have stayed set and
   * silenced it. It has to be the step and not the press for a second reason too: onboarding can
   * now be opened FOR the customer, without them ever pressing it (see markPlaidHandoff), and
   * before this they would finish linking and be met by a modal telling them to go and link.
   */
  const awaitingReview =
    application?.application_step === ApplicationStep.PendingReview;
  const { userRecord } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Clear the localStorage flag when customer becomes active
  useEffect(() => {
    if (userRecord && !isPreActive && userRecord.userType === Entity.Customer) {
      safeRemoveItem('hasClickedOnboardingReminder');
    }
  }, [userRecord, isPreActive]);

  // Set up 3-minute reminder interval for pre-active users
  useEffect(() => {
    const hasClickedOnboarding = safeGetItem('hasClickedOnboardingReminder');

    if (
      userRecord &&
      isPreActive &&
      userRecord.userType === Entity.Customer &&
      !hasClickedOnboarding &&
      !awaitingReview
    ) {
      const intervalId = setInterval(() => {
        setVisible(true);
      }, REMIND_LATER_MS);

      return () => {
        clearInterval(intervalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord, awaitingReview]);

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const handleGoToOnboarding = useCallback(() => {
    if (!application?.application_step) {
      setVisible(false);
      if (location.pathname !== '/dashboard/home') {
        navigate('/dashboard/home');
      }
      return;
    }

    safeSetItem('hasClickedOnboardingReminder', 'true');
    setVisible(false);

    const currentPath = location.pathname;
    const applicationStep = application.application_step;

    if (applicationStep === ApplicationStep.PendingReview) {
      if (currentPath !== '/dashboard/home') {
        navigate('/dashboard/home');
      }
      return;
    }

    if (currentPath === '/dashboard/home') {
      navigate('/dashboard/home', {
        replace: true,
        state: { openModal: applicationStep },
      });
    } else {
      navigate('/dashboard/home', {
        state: { openModal: applicationStep },
      });
    }
  }, [application, location.pathname, navigate]);

  if (!visible || !isPreActive || !application || awaitingReview) {
    return null;
  }

  const isPendingReview =
    application.application_step === ApplicationStep.PendingReview;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="animate-in fade-in zoom-in relative mx-4 w-full max-w-lg duration-300">
        {/* Modal Card */}
        <div className="relative overflow-hidden rounded-[4px] bg-white shadow-2xl">
          {/* Account Activation Banner */}
          <div className="relative overflow-hidden bg-gradient-to-br from-[#1D213B] to-[#373E6D] px-8 py-5">
            <div className="relative flex items-center justify-center gap-3">
              <div className="rounded-full border border-white/10 bg-white/20 p-2.5">
                <SparkleIcon size={24} weight="bold" className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">
                Account Activation
              </h3>
            </div>
          </div>

          {/* Content Section */}
          <div className="px-8 py-6">
            {/* Main Message */}
            <div className="mb-4 text-center">
              <h2 className="mb-2 text-2xl font-bold text-gray-900">
                Unlock Your Full Account
              </h2>
              <p className="text-base leading-relaxed text-gray-600">
                You're currently viewing preview data. Complete your onboarding
                steps and receive bank approval to activate your account.
              </p>
            </div>

            {/* Benefits List */}
            <div className="mb-4 space-y-2 rounded-xl bg-gray-50 px-6 py-3">
              <div className="flex items-start gap-3 py-1">
                <CheckCircleIcon
                  size={22}
                  weight="fill"
                  className="mt-0.5 flex-shrink-0 text-secondary"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Access real financial data
                  </p>
                  <p className="text-xs text-gray-500">
                    Track your actual business performance
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 py-1">
                <CheckCircleIcon
                  size={22}
                  weight="fill"
                  className="mt-0.5 flex-shrink-0 text-secondary"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Unlock cash advances, roadside assistance, and more
                  </p>
                  <p className="text-xs text-gray-500">
                    Track spending & get funding when you need it most
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 py-1">
                <CheckCircleIcon
                  size={22}
                  weight="fill"
                  className="mt-0.5 flex-shrink-0 text-secondary"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Access exclusive truck rentals
                  </p>
                  <p className="text-xs text-gray-500">
                    Get back on the road faster with discounted rates
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              {isPendingReview ? (
                <button
                  onClick={handleClose}
                  className="group relative overflow-hidden rounded-[4px] bg-secondary px-6 py-4 font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <SparkleIcon size={20} weight="fill" />
                    Explore TACH
                  </span>
                  <div className="absolute inset-0 -translate-x-full transform bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"></div>
                </button>
              ) : (
                <>
                  <button
                    onClick={handleGoToOnboarding}
                    className="group relative overflow-hidden rounded-[4px] bg-secondary px-6 py-4 font-bold text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      <SparkleIcon size={20} weight="fill" />
                      Complete Onboarding Now
                    </span>
                    <div className="absolute inset-0 -translate-x-full transform bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"></div>
                  </button>

                  <button
                    onClick={handleClose}
                    className="rounded-[4px] border-2 border-gray-200 bg-white px-6 py-3 font-semibold text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98]"
                  >
                    Finish Later
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
